/**
 * Licensed Materials - Property of IBM
 * IBM Watson Analytics (C) Copyright IBM Corp. 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../../../lib/@waca/core-client/js/core-client/ui/core/View',
	'../../../../features/dashboard/queryService/QueryServiceUtils',
	'../../../../util/DashboardFormatter',
	'../../../../widgets/livewidget/nls/StringResources',
	'@ba/ba-ui-carbon-toolkit',
	'@ba/ba-ui-common-components/lib/filters',
	'react',
	'react-dom',
	'underscore'
], function(View, QueryServiceUtils, Formatter, StringResources, UI_TOOLKIT, UICommon, React, ReactDOM, _) {

	const { NonNumericFilter } = UICommon;
	const { Dialog, Root } = UI_TOOLKIT;

	/**
	 * This class renders non numeric prompt dialog, and handles click events.
	 */
	class NonNumericPromptDialog extends View {

		/**
		 * prop-types are defined in:
		 * [NonNumericFilter Component]{@link https://github.ibm.com/BusinessAnalytics/ba-ui-common/blob/master/src/filters/NonNumericFilter.tsx}
		 *
		 */
		constructor(options) {
			super(options);

			this.dashboard = options.dashboard;
			this.name = options.name;
			this.dataSourceId = options.dataSourceId;
			const dataSource = this.dashboard.getFeature('DataSources').getDataSource(this.dataSourceId);
			this.columnId = options.columnId;
			this.singleSelect = options.singleSelect;
			this.mdColumn = dataSource && dataSource.getMetadataColumn(this.columnId);
			this.queryService = this.dashboard.getFeature('QueryService');

			const userPreferences = this.dashboard.getGlassCoreSvc('.UserProfile').preferences;
			this.locale = userPreferences.productLocale || 'en';
			this.timeZoneID = userPreferences.timeZoneID || 'Etc/UTC';

			// Convert array of selected values to object
			this.selectedValues = options.values ? options.values.reduce((valueObjList, valueObj) => {
				valueObj.selected = true;
				valueObjList[valueObj.value] = valueObj;
				return valueObjList;
			}, {}) : {};
			this.valueList = [];
			this._onOk = options.onOk;
			this._onCancel = options.onCancel;

			this.props = {
				// dialog properties
				title: this.name,
				actionTitle: StringResources.get('promptTitle'),
				filterSelectionType: 'member',
				locale: this.locale,
				invertDisabled: true,
				totalValues: (this.valueList.length || 0),
				selectedValuesCount: _.size(this.selectedValues),
				loading: true,
				readOnly: false,
				allowSearch: true,
				allowCondition: false,
				isDirty: false,
				isSearching: false,

				// dialog call backs
				onDone: this._onDone.bind(this),
				onApply: this._onDone.bind(this),
				onClear: this._onClear.bind(this),
				onClose: this._onClose.bind(this),
				onSearch: this._onSearch.bind(this),
				onClick: this._onClick.bind(this),
				getValue: this._getValue.bind(this)
			};

			this.dialogProps = {
				ariaLabel: StringResources.get('promptDialog', { promptType: 'Search and pick' }),
				className: 'prompts',
				width: '400px',
				height: '500px',
				clickaway: false
			};
		}

		open() {
			return this._getColumnValues().then(values => {
				this.valueList = values;
				return this._setProps({
					selectedValuesCount: _.size(this.selectedValues),
					totalValues: this.valueList.length
				});
			});
		}

		_close() {
			this.closed = true;
			this.selectedValues = null;
			this.valueList.length = 0;
			const containerNode = this._getContainerNode();
			if (containerNode) {
				ReactDOM.unmountComponentAtNode(containerNode);
			}
			this.$el.remove();
			this.$el = null;
			super.remove();
		}

		_getContainerNode() {
			return this.$el && this.$el[0];
		}

		_getColumnValues(searchTerm) {
			let searchTermStr;
			if (typeof searchTerm === 'string' && searchTerm.length > 0) {
				searchTermStr = [searchTerm];
			}
			return this.queryService.getColumnValues(this.dataSourceId, [this.columnId], [], null, searchTermStr).then((queryResult) => {
				const isProperty = this.mdColumn && this.mdColumn.isProperty();
				const values = isProperty ? QueryServiceUtils.extractQueryResultDataValuesForProperty(queryResult) : QueryServiceUtils.extractListOfQueryResultData(queryResult);
				// Update values with existing selections
				values.forEach(value => {
					if (this.selectedValues[value.value]) {
						value.selected = true;
					}
				});
				return values;
			}).catch((e) => {
				throw(e);
			});
		}

		_getValue(rowIdx) {
			const entry = this.valueList[rowIdx];
			let value;
			if (entry) {
				value = {
					id: rowIdx,
					caption: Formatter.format(entry.d),
					selected: !!entry.selected
				};
			} else {
				value = {};
			}
			return value;
		}

		_render() {
			const element = (
				<Dialog
					{...this.dialogProps}
				>
					<Root>
						<NonNumericFilter {...this.props}/>
					</Root>
				</Dialog>
			);
			ReactDOM.render(element, this._getContainerNode());
		}

		_onSearch(payload) {
			this._setProps({ isSearching: true });
			return this._getColumnValues(payload)
				.then(values => {
					this.valueList = values;
					this._setProps({
						isSearching: false,
						selectedValuesCount: _.size(this.selectedValues),
						totalValues: this.valueList.length
					});
				});
		}

		_onClick(payload) {
			const rowIdx = payload.id;
			const valueObj = this.valueList[rowIdx];
			let isDirty;
			if (valueObj.selected) {
				delete valueObj.selected;
				delete this.selectedValues[valueObj.value];
				isDirty = _.size(this.selectedValues) !== 0;
			} else {
				// Clear other selection first in case of single selection prompts
				if (this.singleSelect) {
					for (let key in this.selectedValues) {
						if (this.selectedValues[key].selected) {
							this.valueList.forEach(valueObj => delete valueObj.selected);
							delete this.selectedValues[key];
						}
					}
				}
				valueObj.selected = true;
				this.selectedValues[valueObj.value] = valueObj;
				isDirty = true;
			}

			this._setProps({
				isDirty,
				selectedValuesCount: _.size(this.selectedValues)
			});
		}

		_onDone() {
			if (!this.closed) {
				this._onOk(Object.values(this.selectedValues));
				this._close();
			}
		}

		_onClear() {
			this.selectedValues = {};
			this.valueList.forEach(valueObj => delete valueObj.selected);
			this._setProps({
				isDirty: false,
				selectedValuesCount: _.size(this.selectedValues)
			});
		}

		_onClose() {
			this._onCancel();
			this._close();
		}

		/**
		 * Re-render whenever a property is updated.
		 * @param {Object} partialProp
		 */
		_setProps(partialProps) {
			this.props.loading = false;
			Object.assign(this.props, partialProps);
			this._render();
		}
	}

	return NonNumericPromptDialog;
});
